/**
 * BrokerAnalyticsTab — Charts and stats derived from broker's existing data.
 * No new API endpoints. Pure client-side computation via brokerAnalytics utils.
 */

import React, { useMemo } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, Cell,
} from 'recharts'
import { TrendingUp, DollarSign, Users, Target } from 'lucide-react'
import type { Connection, Lead, Application } from '../api/brokerApi'
import {
  computeLeadPipeline,
  computeApplicationsByPhase,
  computeMonthlyActivity,
  computeConversionRate,
  computeAverageLoanAmount,
  computePipelineValue,
} from '../utils/brokerAnalytics'

interface Props {
  connections: Connection[]
  leads: Lead[]
  applications: Application[]
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`
  return `$${value}`
}

export default function BrokerAnalyticsTab({ connections, leads, applications }: Props) {
  const leadPipeline = useMemo(() => computeLeadPipeline(leads), [leads])
  const appPhases = useMemo(() => computeApplicationsByPhase(applications), [applications])
  const monthlyActivity = useMemo(() => computeMonthlyActivity(leads, connections), [leads, connections])
  const conversionRate = useMemo(() => computeConversionRate(leads), [leads])
  const avgLoanAmount = useMemo(() => computeAverageLoanAmount(applications), [applications])
  const pipelineValue = useMemo(() => computePipelineValue(applications), [applications])

  return (
    <div data-testid="broker-analytics-tab" className="space-y-6">
      {/* Summary Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Target className="w-5 h-5 text-blue-600" />}
          label="Conversion Rate"
          value={`${conversionRate}%`}
          bg="bg-blue-50"
        />
        <StatCard
          icon={<DollarSign className="w-5 h-5 text-green-600" />}
          label="Avg Loan Amount"
          value={formatCurrency(avgLoanAmount)}
          bg="bg-green-50"
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5 text-purple-600" />}
          label="Pipeline Value"
          value={formatCurrency(pipelineValue)}
          bg="bg-purple-50"
        />
        <StatCard
          icon={<Users className="w-5 h-5 text-amber-600" />}
          label="Total Leads"
          value={String(leads.length)}
          bg="bg-amber-50"
        />
      </div>

      {/* Charts Row 1: Lead Pipeline + Application Phases */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Pipeline Funnel */}
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Lead Pipeline</h3>
          {leads.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No leads yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={leadPipeline} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="status" type="category" width={90} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Applications by Phase */}
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Applications by Phase</h3>
          {applications.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No applications yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={appPhases}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="phase" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {appPhases.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts Row 2: Monthly Activity */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Monthly Activity (Last 6 Months)</h3>
        {leads.length === 0 && connections.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No activity data yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthlyActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="leads" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="connections" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

function StatCard({ icon, label, value, bg }: { icon: React.ReactNode; label: string; value: string; bg: string }) {
  return (
    <div className="card flex items-center gap-4">
      <div className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </div>
  )
}
